<script context="module">
    import { get } from "svelte/store";

    import {
        insuranceFund,
        workersCompensation,
        trafficAccidentAuthorities,
        bupaPractices,
        mbsAllItemsList,
        asaAllItemsList,
        allGroups,
        user,
        defaultFilters,
        prePopulateAccountOptions,
        postOpSurveys,
        preOpQuestionnaires,
        preOpInstructions,
        providersSpecialityCodes,
    } from "../stores";

    import { apiMethods } from "../Services/index.svelte";

    import {
        isObjectEmpty,
        showError,
        useTableScrollMemory,
    } from "./utilFunctions.svelte";

    import { DirectUserRoles } from "./utilEnums.svelte";
    import { FollowUpPageRefreshInterval } from "./utilConstants.svelte";

    let isCachingInsuranceFunds = false;
    let cachingInsuranceFundPromise = null;
    export const cacheInsuranceFunds = async () => {
        if (isCachingInsuranceFunds) return cachingInsuranceFundPromise;

        if (get(insuranceFund).length) return;

        isCachingInsuranceFunds = true;

        cachingInsuranceFundPromise = apiMethods.getMethod
            .getInsuranceFund(0, 999999)
            .then((res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to load insurance funds, please refresh the page and try again and contact support if the issue persists.",
                    );
                else insuranceFund.set([...res.results]);
            })
            .catch((err) => showError(err))
            .finally(() => {
                isCachingInsuranceFunds = false;
                cachingInsuranceFundPromise = null;
            });

        return cachingInsuranceFundPromise;
    };

    export const getInsuranceFundById = async (id) => {
        if (!id) showError("Missing fund id");

        let funds = get(insuranceFund);

        if (!funds.length) await cacheInsuranceFunds();

        funds = get(insuranceFund);

        const res = funds.find((fund) => fund.id == id);

        if (!res) showError(`No funds found with given id: ${id}`);

        return res;
    };

    export const cacheWorkersCompensation = async (reCache = false) => {
        if (get(workersCompensation).length && !reCache) return;

        await apiMethods.getMethod
            .getWorkCoverAuthorities(0, 999999)
            .then((res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to load workers compensation, please refresh the page and try again and contact support if the issue persists.",
                    );
                else workersCompensation.set([...res.results]);
            })
            .catch((err) => showError(err));
    };

    export const getWorkerCompensationById = async (id) => {
        if (!id) showError("Missing compensation id");

        let compensations = get(workersCompensation);

        if (!compensations.length) await cacheWorkersCompensation();

        compensations = get(workersCompensation);

        const res = compensations.find((compensation) => compensation.id == id);

        if (!res)
            showError(`No worker compensation found with given id: ${id}`);

        return res;
    };

    export const cacheTrafficAccidentAuthorities = async (reCache = false) => {
        if (get(trafficAccidentAuthorities).length && !reCache) return;

        await apiMethods.getMethod
            .getTrafficAccidentAuthorities(0, 999999)
            .then((res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to load traffic accident authorities, please refresh the page and try again and contact support if the issue persists.",
                    );
                else trafficAccidentAuthorities.set([...res.results]);
            })
            .catch((err) => showError(err));
    };

    export const getTrafficAccidentAuthorityById = async (id) => {
        if (!id) showError("Missing authority id");

        let authorities = get(trafficAccidentAuthorities);

        if (!authorities.length) await cacheTrafficAccidentAuthorities();

        authorities = get(trafficAccidentAuthorities);

        const res = authorities.find((authority) => authority.id == id);

        if (!res)
            showError(
                `No traffic accident authorities found with given id: ${id}`,
            );

        return res;
    };

    export const cacheBupaPractices = async () => {
        if (get(bupaPractices).length) return;

        await apiMethods.getMethod
            .getBupaPractices(0, 999999)
            .then((res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to load bupa practice ids, please refresh the page and try again and contact support if the issue persists.",
                    );
                else {
                    const results = res.results.map((bp) => ({
                        ...bp,
                        practiceId: Number(bp.practiceId),
                    }));
                    bupaPractices.set([...results]);
                }
            })
            .catch((err) => showError(err));
    };

    export const getBupaPracticeById = async (id) => {
        if (!id) showError("Missing bupa practice id");

        let practices = get(bupaPractices);

        if (!practices.length) await cacheBupaPractices();

        practices = get(bupaPractices);

        const res = practices.find((practice) => practice.id == id);

        if (isObjectEmpty(res))
            showError(`No bupa practice found with given id: ${id}`);

        return res;
    };

    export const cacheMbsItems = async () => {
        if (get(mbsAllItemsList).length) return;

        await apiMethods.getMethod
            .getMBSItemsWithoutRebate(0, 999999)
            .then((res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to load MBS Items, please refresh the page and try again and contact support if the issue persists.",
                    );
                else mbsAllItemsList.set([...res.results]);
            })
            .catch((err) => showError(err));
    };

    export const cacheAsaItems = async () => {
        if (get(asaAllItemsList).length) return;

        await apiMethods.getMethod
            .getASAItems(0, 999999)
            .then((res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to load ASA Items, please refresh the page and try again and contact support if the issue persists.",
                    );
                else asaAllItemsList.set([...res.results]);
            })
            .catch((err) => showError(err));
    };

    export const cacheAllGroups = async (force = false) => {
        if (get(allGroups).length && !force) return;

        await apiMethods.getMethod
            .getGroups(0, 999999)
            .then((res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to load groups detail, please refresh the page and try again and contact support if the issue persists.",
                    );
                else allGroups.set([...res.results]);
            })
            .catch((err) => showError(err));
    };

    let isCachingDefaultFilters = false;
    let cachingPromise = null;
    export const cacheDefaultFilters = async () => {
        if (isCachingDefaultFilters) return cachingPromise;

        isCachingDefaultFilters = true;

        const searchParams = {};

        if (get(user).role == DirectUserRoles.Manager) {
            searchParams["filter[groupID]"] = get(user).groupId;
        } else if (
            get(user).role == DirectUserRoles.Admin ||
            get(user).role == DirectUserRoles.Support
        ) {
            delete searchParams["filter[groupID]"];
        } else {
            defaultFilters.set({});
            isCachingDefaultFilters = false;
            cachingPromise = null;
            return;
        }

        cachingPromise = apiMethods.getMethod
            .getDefaultFilters(0, 999999, searchParams)
            .then((res) => {
                const filters = get(defaultFilters);
                defaultFilters.set({
                    ...filters,
                    userFilter: res.results,
                });
                return res.results;
            })
            .finally(() => {
                isCachingDefaultFilters = false;
                cachingPromise = null;
            });

        return cachingPromise;
    };

    let isCachingAccountPrePopulateSearchOptions = false;
    let cachingAccountPrePopulateSearchOptionsPromise = null;
    let accountPrePopulateSearchOptionsCached = false;
    export const cacheAccountPrePopulateSearchOptions = async (
        reCache = false,
    ) => {
        if (isCachingAccountPrePopulateSearchOptions)
            return cachingAccountPrePopulateSearchOptionsPromise;

        if (
            !reCache &&
            (get(prePopulateAccountOptions).length ||
                accountPrePopulateSearchOptionsCached)
        )
            return;

        isCachingAccountPrePopulateSearchOptions = true;

        cachingAccountPrePopulateSearchOptionsPromise =
            await apiMethods.getMethod
                .getAccountPrePopulateSearchOptions()
                .then((res) => {
                    if (!res || res.error)
                        showError(
                            res.error ??
                                "Failed to load doctor search options, please refresh the page and try again and contact support if the issue persists.",
                        );
                    else {
                        const options = [];

                        res.results.map((option) => {
                            const { id, fullName, preferredName, status } =
                                option;
                            options.push({
                                key: id,
                                value: `${fullName} ${preferredName ? "(" + preferredName + ")" : ""} ${status == 1 ? " (Demo)" : ""}`,
                                ...option,
                            });
                        });

                        prePopulateAccountOptions.set([...options]);
                    }
                })
                .finally(() => {
                    isCachingAccountPrePopulateSearchOptions = false;
                    cachingAccountPrePopulateSearchOptionsPromise = null;
                    accountPrePopulateSearchOptionsCached = true;
                });

        return cachingAccountPrePopulateSearchOptionsPromise;
    };

    let isCachingPreOpQuestionnaire = false;
    let cachingPreOpQuestionnairePromise = null;
    let preOpQuestionnaireCached = false;
    export const cachePreOpQuestionnaire = async (reCache = false) => {
        if (isCachingPreOpQuestionnaire)
            return cachingPreOpQuestionnairePromise;

        if (
            !reCache &&
            (get(preOpQuestionnaires).length || preOpQuestionnaireCached)
        )
            return;

        isCachingPreOpQuestionnaire = true;

        const searchParams = {};
        const userDetails = get(user);

        if (userDetails.role == DirectUserRoles.Manager)
            searchParams["filter[groupId]"] = `0,${userDetails.groupId}`;
        else if (
            userDetails.role == DirectUserRoles.Admin ||
            userDetails.role == DirectUserRoles.Support
        )
            delete searchParams["filter[groupId]"];
        else if (userDetails.groupId)
            searchParams["filter[groupId]"] = `0,${userDetails.groupId}`;

        cachingPreOpQuestionnairePromise = apiMethods.getMethod
            .getPreOpQuestionnaires(0, 999999, searchParams)
            .then((res) => {
                if (!res || res.error) {
                    showError(
                        res.error ??
                            "Failed to load Pre-Op Questionnaire, please refresh the page and try again and contact support if the issue persists.",
                    );
                } else preOpQuestionnaires.set([...res.results]);
            })
            .finally(() => {
                isCachingPreOpQuestionnaire = false;
                cachingPreOpQuestionnairePromise = null;
                preOpQuestionnaireCached = true;
            });

        return cachingPreOpQuestionnairePromise;
    };

    let isCachingPreOpInstructions = false;
    let cachingPreOpInstructionsPromise = null;
    let preOpInstructionsCached = false;
    export const cachePreOpInstructions = async (reCache = false) => {
        if (isCachingPreOpInstructions) return cachingPreOpInstructionsPromise;

        if (
            !reCache &&
            (get(preOpInstructions).length || preOpInstructionsCached)
        )
            return;

        isCachingPreOpInstructions = true;

        const searchParams = {};

        cachingPreOpInstructionsPromise = apiMethods.getMethod
            .getPreOpInstructions(0, 999999, searchParams)
            .then((res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to load Pre-Op Instructions, please refresh the page and try again and contact support if the issue persists.",
                    );
                else preOpInstructions.set([...res.results]);
            })
            .finally(() => {
                isCachingPreOpInstructions = false;
                cachingPreOpInstructionsPromise = null;
                preOpInstructionsCached = true;
            });

        return cachingPreOpInstructionsPromise;
    };

    export const getFilteredPreOpInstructions = async (config = {}) => {
        await cachePreOpInstructions();

        if (!get(preOpInstructions)?.length || !config || isObjectEmpty(config))
            return [];

        const { id: dId, groupId: dGroupId, checkGroupOnly } = config;

        return checkGroupOnly
            ? get(preOpInstructions).filter(
                  ({ groupId }) => !groupId || groupId == dGroupId,
              )
            : get(preOpInstructions).filter(
                  ({ accountId, groupId }) =>
                      (!accountId && !groupId) ||
                      (!accountId && groupId === dGroupId) ||
                      (!groupId && accountId == dId) ||
                      (accountId == dId && groupId == dGroupId),
              );
    };

    let isCachingPostOpSurvey = false;
    let cachingPostOpSurveyPromise = null;
    let postOpSurveysCached = false;
    export const cachePostOpSurvey = async (reCache = false) => {
        if (isCachingPostOpSurvey) return cachingPostOpSurveyPromise;

        if (!reCache && (get(postOpSurveys).length || postOpSurveysCached))
            return;

        isCachingPostOpSurvey = true;

        const searchParams = {};
        const userDetails = get(user);

        if (userDetails.role == DirectUserRoles.Manager)
            searchParams["filter[groupID]"] = `0,${userDetails.groupId}`;
        else if (
            userDetails.role == DirectUserRoles.Admin ||
            userDetails.role == DirectUserRoles.Support
        )
            delete searchParams["filter[groupID]"];
        else if (userDetails.groupId)
            searchParams["filter[groupID]"] = `0,${userDetails.groupId}`;

        cachingPostOpSurveyPromise = apiMethods.getMethod
            .getPostOpSurvey(0, 999999, searchParams)
            .then((res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to load Post-Op Survey, please refresh the page and try again and contact support if the issue persists.",
                    );
                else postOpSurveys.set([...res.results]);
            })
            .finally(() => {
                isCachingPostOpSurvey = false;
                cachingPostOpSurveyPromise = null;
                postOpSurveysCached = true;
            });

        return cachingPostOpSurveyPromise;
    };

    export let startRefreshTable = (
        tableConfig = {
            tableIntervalRef: null,
            refreshInterval,
        },
        reloadFunc = () => {},
    ) => {
        if (!tableConfig) return;

        const { refreshInterval } = tableConfig;

        tableConfig.tableIntervalRef = setInterval(() => {
            reloadFunc();
        }, refreshInterval ?? FollowUpPageRefreshInterval);

        return tableConfig.tableIntervalRef;
    };

    export let stopRefreshTable = (tableInterval) => {
        if (tableInterval) clearInterval(tableInterval);
    };

    let isCachingProviderSpecialityCodes = false,
        cachingProviderSpecialityCodesPromise = null,
        providerSpecialityCodesCached = false;
    export let cacheProviderSpecialityCodes = async (reCache = false) => {
        if (isCachingProviderSpecialityCodes)
            return cachingProviderSpecialityCodesPromise;

        if (
            !reCache &&
            (get(providersSpecialityCodes).length ||
                providerSpecialityCodesCached)
        )
            return;

        isCachingProviderSpecialityCodes = true;

        cachingProviderSpecialityCodesPromise = apiMethods.getMethod
            .getProvidersSpecialityCodes()
            .then((res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to load provider speciality codes, please refresh the page and try again and contact support if the issue persists.",
                    );
                else providersSpecialityCodes.set([...res]);
            })
            .finally(() => {
                isCachingProviderSpecialityCodes = false;
                cachingProviderSpecialityCodesPromise = null;
                providerSpecialityCodesCached = true;
            });

        return cachingProviderSpecialityCodesPromise;
    };
</script>
